<template>
  <div class="relative min-h-screen">
    <div class="flex justify-center" v-if="isFirst">
      <bar-loader
        class="my-8"
        color="#4db1fa"
        :width="320"
      />
    </div>

    <div class="relative flex flex-col items-center text-center pt-6 pb-40 px-8 md:px-32" v-else>
      <img :src="post.image" class="object-cover w-full max-w-lg mx-auto h-64" v-if="post.image">

      <p class="text-2xl mt-2 max-w-xl">
        {{ post.title }}
        <button
          class="btn-small h-6"
          :disabled="post.is_owner"
          v-if="isAuthenticated"
          @click="report"
        >
          Flag
        </button>
      </p>
      <p class="text-sm mt-1">{{ dateFormatted }}</p>
      <p class="text-black my-3">{{ post.body }}</p>
      <p class="text-sm">Posted by: {{ post.user }}</p>

      <!-- Comments -->
      <section class="my-8 w-full max-w-lg">
        <form
          class="flex flex-col w-full"
          v-if="isAuthenticated"
          @submit.prevent="handleComment"
        >
          <base-input
            v-model="comment.body"
            v-validate="'required|max:255'"
            name="comment"
            class="h-20"
            placeholder="Type a comment"
            type="textarea"
            :message="errors.first('comment')"
          />
          <button class="btn btn-blue mt-4 ml-auto" :disabled="isLoading">Leave comment</button>
        </form>

        <div class="flex justify-center" v-if="isFirstComments">
          <beat-loader
            class="my-8"
            color="#4db1fa"
          />
        </div>

        <div class="w-full mt-12" v-else>
          <div
            v-for="comment in comments"
            :key="comment.id"
            class="flex my-6"
          >
            <figure class="rounded-full flex flex-none items-center justify-center bg-blue-500 h-12 w-12">
              <img src="/images/person.png">
            </figure>
            <div class="text-left ml-4 flex-grow">
              <p>{{ comment.user }}</p>
              <p class="text-sm">{{ comment.body }}</p>
            </div>
            <button
              class="btn-small h-6"
              :disabled="comment.is_owner"
              v-if="isAuthenticated"
              @click="reportComment(post, comment)"
            >
              Flag
            </button>
          </div>
        </div>
      </section>
    </div>

    <base-footer class="absolute bottom-0" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import PostService from '@/services/PostService';
import { mapGetters } from 'vuex';

export default {
  data() {
    return {
      post: {},
      comment: {},
      comments: [],
      currentPage: null,
      lastPage: 1,
      isFirst: true,
      isFirstComments: true,
      isLoading: false,
    };
  },
  watch: {
    async currentPage() {
      await this.fetch();
    }
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),

    dateFormatted() {
      return dayjs(this.post.date).format('MMM DD, YYYY');
    },
  },
  async created() {
    try {
      const { data: { data } } = await PostService.show(this.$route.params.id);

      this.post = data;
      this.currentPage = 1;

      this.isFirst = false;
      this.$parent.$on('scroll_ended', this.nextPage);
    } catch(e) {
      this.$router.push({ name: 'posts' });
    }
  },
  methods: {
    async fetch() {
      const { data: { data, meta } } = await PostService.getComments(this.post, {
        params: {
          q: this.searchText,
          page: this.currentPage,
        },
      });

      this.comments = this.comments.concat(data);
      this.lastPage = meta.last_page;

      if (this.isFirstComments) {
        this.isFirstComments = false;
      }
    },

    nextPage() {
      if (this.currentPage === this.lastPage) {
        return;
      }
      this.currentPage++;
    },

    async handleComment() {
      try {
        this.isLoading = true;

        if (! await this.$validator.validateAll()) {
          return;
        }

        await PostService.comment(this.post, this.comment);

        this.comment = {};
        this.isFirstComments = true;

        // Reload comments
        if (this.currentPage === 1) {
          this.comments = [];
          await this.fetch();
        } else {
          this.currentPage = 1;
        }
      } catch (e) {
        this.$setErrorsFromLaravel(e.response.data);
      } finally {
        this.isLoading = false;
      }
    },

    async report() {
      try {
        await PostService.report(this.post);
        this.$toasted.show('Post flagged successfully.');
      } catch (e) {
        // Ignore
      }
    },

    async reportComment(post, comment) {
      try {
        await PostService.reportComment(post, comment);
        this.$toasted.show('Comment flagged successfully.');
      } catch (e) {
        // Ignore
      }
    },
  },
};
</script>
